import React, { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Wifi, Car, Coffee, Waves, Bath, Utensils, Plane, Building2, Star, Heart, CheckCircle } from "lucide-react";

import { TokenContext } from "../components/TokenContext";
import { callProxy } from "../api/api";
import { fetchPackages } from "../api/packages";
import { likeApartment } from "../api/apartments";
import { getApartmentImage } from "../utils/getApartmentImage";
import useSEO from "../hooks/useSEO";

import Sidebar from "../components/Sidebar";
import MobileFilters from "../components/MobileFilters";
import ApartmentCard from "../components/ApartmentCard";
import PackagePage from "./PackagePage";

import { safeString } from "../utils/langUtils";

const AMENITY_ICONS = {
  "free wifi": Wifi, "private parking": Car, "good breakfast": Coffee,
  "swimming pool": Waves, "swimming pools": Waves, "private bathroom": Bath,
  "balcony": Building2, "restaurants": Utensils, "airport shuttle": Plane,
  "ocean view": Waves,
};


const getLang = (field, lang = 'en') => {
  if (!field) return "";
  // If it's already a string, return it
  if (typeof field === "string") return field;
  // If it's an object, return the requested language or fallback to 'en'
  return field[lang] || field['en'] || Object.values(field)[0] || "";
};


/* --- CHILD COMPONENT --- */
function PremiumMobileCard({ apartment }) {
  const navigate = useNavigate();
  // ✅ Price comes directly from backend now
  const price = apartment.price ?? "—"; 

  const [liked, setLiked] = useState(
    apartment.likedBy?.includes(localStorage.getItem("visitorId")) || false
  );

  const { slug, name, city, country, likesCount, amenities } = apartment;
  const imageUrl = useMemo(() => getApartmentImage(apartment), [apartment]);

  const mobileAmenities = useMemo(() => {
    if (!amenities) return [];
    return amenities.slice(0, 8).map((item) => {
      const key = item.toLowerCase();
      return { label: item, Icon: AMENITY_ICONS[key] || CheckCircle };
    });
  }, [amenities]);

  const handleLike = async (e) => {
    e.stopPropagation();
    try {
      const res = await likeApartment(slug);
      setLiked(res.data.liked);
    } catch (err) { console.error(err); }
  };

  return (
    <div onClick={() => navigate(`/apartments/${slug}`)} className="bg-white rounded-xl shadow border w-full overflow-hidden active:scale-[0.98] transition-all duration-150">
      <div className="relative aspect-[4/3] w-full">
        <img src={imageUrl} alt={name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" onError={(e) => e.currentTarget.src = "/assets/images/placeholder.jpg"} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
        <button onClick={handleLike} className="absolute top-2 right-2 flex items-center gap-1.5 bg-white/95 backdrop-blur-md px-2 py-1 rounded-full shadow">
          <Heart size={14} className={`${liked ? "text-red-500 fill-red-500" : "text-gray-700"}`} />
          {likesCount > 0 && <span className="text-[11px] font-semibold text-gray-800">{likesCount}</span>}
        </button>
        <span className="absolute top-2 left-2 bg-blue-600 text-white text-[10px] px-2 py-1 rounded z-10">Genius Deal</span>
      </div>
      <div className="p-3 space-y-1">
        <h3 className="text-sm font-semibold text-gray-900 truncate">{name}</h3>
        <p className="text-xs text-gray-500 truncate">{city}, {country}</p>
        {mobileAmenities.length > 0 && (
          <div className="sm:hidden -mx-3 mt-2 mb-1">
            <div className="flex gap-2 overflow-x-auto px-3 scrollbar-hide">
              {mobileAmenities.map(({ label, Icon }, idx) => (
                <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-[11px] whitespace-nowrap shrink-0">
                  <Icon className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-between items-end pt-2">
          <div>
            <p className="font-bold text-[17px] text-gray-900">
              AED {typeof price === "number" ? price.toLocaleString() : price}
            </p>
            <p className="text-[11px] text-gray-500">per night</p>
          </div>
          <button className="bg-[#3a2a00] text-white text-xs font-medium px-3 py-1.5 rounded-lg">See availability</button>
        </div>
      </div>
    </div>
  );
}


/* --- MAIN PAGE --- */
export default function AllApartmentPage() {
  const { token } = useContext(TokenContext);
  
  useEffect(() => {
    if (!localStorage.getItem("visitorId")) {
      localStorage.setItem("visitorId", crypto.randomUUID());
    }
  }, []);

  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  
  const [filters, setFilters] = useState({
    maxBudget: 20000, 
    minPrice: 0,
    rating: "any",
    types: [],
    amenities: [],
  });

  const [mobilePackages, setMobilePackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);

  // useEffect(() => {
  //   let mounted = true;
  //   fetchPackages().then((data) => { if (mounted) setMobilePackages(data || []); })
  //     .finally(() => { if (mounted) setPackagesLoading(false); });
  //   return () => { mounted = false; };
  // }, []);

  useEffect(() => {
  let mounted = true;
  fetchPackages().then((data) => { 
    if (mounted && data) {
      // ✅ NORMALIZE PACKAGES
      const normalizedPackages = data.map(pkg => ({
        ...pkg,
        title: safeString(pkg.title),
        description: safeString(pkg.description)
      }));
      setMobilePackages(normalizedPackages); 
    }
  })
  .finally(() => { if (mounted) setPackagesLoading(false); });
  return () => { mounted = false; };
}, []);

  // ---------------------------------------------------------
  // ✅ FIX 1: SIMPLIFIED FETCH (Backend does the work)
  // ---------------------------------------------------------
// const fetchApartments = useCallback(async () => {
//     setLoading(true);
//     try {
//       // ✅ ONE CALL: The backend now returns { ..., price: 500 }
//       const res = await callProxy("/api/apartments", token);
      
//       // Ensure we pass an array (fallback to empty)
//       setApartments(res.data || []);
      
//     } catch (err) {
//       console.error("Failed to load apartments", err);
//     } finally {
//       setLoading(false);
//     }
//   }, [token]);

//-----------------------
const fetchApartments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callProxy("/api/apartments", token);
      // ✅ Handle both res.data and raw res
      const rawData = res.data || res || []; 

      const normalizedData = Array.isArray(rawData) ? rawData.map(apt => ({
        ...apt,
        name: safeString(apt.name),
        description: safeString(apt.description),
        city: safeString(apt.city),
        country: safeString(apt.country),
        amenities: Array.isArray(apt.amenities) 
          ? apt.amenities.map(amt => safeString(amt)) 
          : []
      })) : [];

      setApartments(normalizedData);
    } catch (err) {
      console.error("Failed to load apartments", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchApartments();
  }, [fetchApartments]);

  const handleApartmentLike = async (slug) => {
    try {
      const res = await likeApartment(slug);
      setApartments((prev) => prev.map((apt) => apt.slug === slug ? { ...apt, likesCount: res.data.likesCount, likedBy: res.data.likedBy } : apt));
    } catch (err) { console.error("Like failed", err); }
  };

const filteredApartments = useMemo(() => {
    return apartments.filter((apt) => {
      const priceToCheck = apt.price || 0;
      
      // 1. Price Filter
      if (priceToCheck < filters.minPrice || priceToCheck > filters.maxBudget) return false;
      
      // 2. Rating Filter
      if (filters.rating !== "any" && (apt.rating ?? 0) < Number(filters.rating)) return false;
      
      // 3. Amenities Filter (Safety check for objects)
      if (filters.amenities.length > 0) {
        const aptAmenities = (apt.amenities || []).map(a => safeString(a).toLowerCase());
        if (!filters.amenities.every((a) => aptAmenities.includes(a.toLowerCase()))) {
          return false;
        }
      }
      
      return true;
    });
  }, [apartments, filters]);


  const handleFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  useSEO({ title: "Luxury Apartments", description: "Holiday homes in Dubai", url: "https://highendholidays.ae/apartments" });

  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <MobileFilters filters={filters} onFilterChange={handleFilterChange} />
        
        <div className="flex gap-6">
          <div className="hidden md:block w-[280px] flex-shrink-0">
            <Sidebar filters={filters} onFilterChange={handleFilterChange} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="hidden lg:block mb-10"><PackagePage /></div>
            
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-2xl font-bold">Dubai: {filteredApartments.length} properties found</h1>
                <p className="text-sm text-gray-500">Browse luxury apartments & serviced stays</p>
              </div>
            </div>

            {loading ? (
              <p className="text-center py-20 text-gray-500">Loading apartments...</p>
            ) : apartments.length === 0 ? (
              <p className="text-center py-10 text-gray-500">No apartments found.</p>
            ) : (
              <>
                {/* MOBILE SWIPER */}
                <div className="sm:hidden -mx-4 overflow-hidden px-4">
                  <h2 className="text-[17px] font-bold text-gray-900 leading-tight mb-3">Deals for the weekend</h2>
                  <Swiper spaceBetween={12} slidesPerView={1.05} resistanceRatio={0.6} className="px-4 pb-4">
                    {filteredApartments.map((apt) => (
                      // ✅ FIX 2: Ensure KEY is unique (Using apt.id from backend fix)
                     <SwiperSlide key={apt._id || apt.id || apt.slug}>
                        <PremiumMobileCard apartment={apt} />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>

                {/* MOBILE OFFERS SECTION (Kept simple) */}
                <div className="mb-5 mt-2 sm:hidden">
                   <h2 className="text-[17px] font-bold text-gray-900 mb-3">Offers</h2>
                   <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
                    {mobilePackages.slice(0, 6).map((pkg) => (
                      <Link key={pkg.id || pkg.slug} to={`/packages/${pkg.slug}`} className="block min-w-[170px]">
                        <div className="h-[140px] relative rounded-xl overflow-hidden shadow-lg">
                           <img src={pkg.image || "/assets/images/holiday/default.jpg"} className="w-full h-full object-cover"/>
                           <div className="absolute bottom-0 p-2 text-white">
                             <span className="font-bold text-yellow-400">${pkg.price}</span>
                           </div>
                        </div>
                      </Link>
                    ))}
                   </div>
                </div>
                
                {/* DESKTOP GRID */}
                <div className={`hidden sm:grid gap-4 ${viewMode === "grid" ? "md:grid-cols-2" : "grid-cols-1"}`}>
                  {filteredApartments.map((apt) => (
                    // ✅ FIX 3: Unique Key Here Too
                  <ApartmentCard 
                      key={apt._id || apt.id || apt.slug} 
                      apartment={apt} 
                      onLike={handleApartmentLike} 
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}